import ProductCell from "@/core/components/ProductCell";
import { useGuardedDataTableActions } from "@/core/helpers/hooks/useGuardedDataTableActions";
import { getImportTemplateConfig } from "@/features/import/config/templateTypeConfig";
import { downloadImportTemplate } from "@/features/import/utils/downloadTemplate";
import { useProductBrandAutocomplete } from "@/features/product-brand/usecases/useProductBrandAutocomplete";
import { useProductCategoryAutocomplete } from "@/features/product-category/usecases/useProductCategoryAutocomplete";
import { useProductDepartmentAutocomplete } from "@/features/product-department/usecases/useProductDepartmentAutocomplete";
// import { useProductDivisionAutocomplete } from "@/features/product-division/usecases/useProductDivisionAutocomplete";
import { useProductSourceAutocomplete } from "@/features/product-source/usecases/useProductSourceAutocomplete";
import { useProductSubCategoryAutocomplete } from "@/features/product-sub-category/usecases/useProductSubCategoryAutocomplete";
import { useProductSubDepartmentAutocomplete } from "@/features/product-sub-department/usecases/useProductSubDepartmentAutocomplete";
import type { PricelistProduct } from "@/generated/graphql";
import {
	type AutocompleteFetcher,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
	toast,
} from "@k3-tech/react-kit";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import {
	DownloadIcon,
	EditIcon,
	EyeIcon,
	PlusIcon,
	RefreshCcwIcon,
	Trash2Icon,
	UploadIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";

export default function PricelistProductTable({
	data,
	rowCount,
	loading,
	onRefresh,
	onFormFilterChange,
	paginationState,
	onPaginationChange,
	onShowAddProduct,
	onShowDetail,
	onShowEdit,
	onShowDelete,
	pricelistID,
	pricelistPeriodID,
}: {
	data: PricelistProduct[];
	rowCount?: number;
	loading: boolean;
	onRefresh: () => void;
	onFormFilterChange?: (values: Record<string, unknown>) => void;
	paginationState: PaginationState;
	onPaginationChange?: (paginationState: PaginationState) => void;
	onShowAddProduct: () => void;
	onShowDetail: (pricelistProduct: PricelistProduct) => void;
	onShowEdit: (pricelistProduct: PricelistProduct) => void;
	onShowDelete: (pricelistProduct: PricelistProduct) => void;
	pricelistID?: string;
	pricelistPeriodID?: string;
}) {
	const navigate = useNavigate();
	const onImport = useCallback(() => {
		if (!pricelistID || !pricelistPeriodID) {
			toast.error("Pricelist period is required to import items");
			return;
		}
		navigate({
			to: "/admin/pricelist/$id/import",
			params: { id: pricelistID },
			search: { periodID: pricelistPeriodID },
		});
	}, [navigate, pricelistID, pricelistPeriodID]);
	const onDownloadTemplate = useCallback(() => {
		const config = getImportTemplateConfig("pricelist_product");
		if (!config) {
			toast.error("Import config missing");
			return;
		}
		downloadImportTemplate("pricelist_products.csv", config.standardKeys);
	}, []);
	const { load: loadProductBrand } = useProductBrandAutocomplete();
	const { load: loadProductCategory } = useProductCategoryAutocomplete();
	const { load: loadProductSubCategory } = useProductSubCategoryAutocomplete();
	const { load: loadProductDepartment } = useProductDepartmentAutocomplete();
	const { load: loadProductSubDepartment } =
		useProductSubDepartmentAutocomplete();
	// const { load: loadProductDivision } = useProductDivisionAutocomplete();
	const { load: loadProductSource } = useProductSourceAutocomplete();

	const productBrandAutocompleteFetcher: AutocompleteFetcher = useCallback(
		async (params) => {
			return loadProductBrand(params.page, params.pageSize, params.search);
		},
		[loadProductBrand],
	);

	const productCategoryAutocompleteFetcher: AutocompleteFetcher = useCallback(
		async (params) => {
			return loadProductCategory(params.page, params.pageSize, params.search);
		},
		[loadProductCategory],
	);

	const productSubCategoryAutocompleteFetcher: AutocompleteFetcher =
		useCallback(
			async (params) => {
				return loadProductSubCategory(
					params.page,
					params.pageSize,
					params.search,
				);
			},
			[loadProductSubCategory],
		);

	const productDepartmentAutocompleteFetchcer: AutocompleteFetcher =
		useCallback(
			async (params) => {
				return loadProductDepartment(
					params.page,
					params.pageSize,
					params.search,
				);
			},
			[loadProductDepartment],
		);

	const productSubDepartmentAutocompleteFetchcer: AutocompleteFetcher =
		useCallback(
			async (params) => {
				return loadProductSubDepartment(
					params.page,
					params.pageSize,
					params.search,
				);
			},
			[loadProductSubDepartment],
		);

	// const productDivisionAutocompleteFetcher: AutocompleteFetcher = useCallback(async (
	// 	params
	// ) => {
	// 	return loadProductDivision(params.page, params.pageSize, params.search);
	// }, [loadProductDivision]);

	const productSourceAutocompleteFetcher: AutocompleteFetcher = useCallback(
		async (params) => {
			return loadProductSource(params.page, params.pageSize, params.search);
		},
		[loadProductSource],
	);

	const columns: ColumnDef<PricelistProduct, unknown>[] = useMemo(
		() =>
			[
				{
					id: "productName",
					header: () => <div className="min-w-[300px]">Product</div>,
					accessorKey: "product.productName",
					cell: ({ row }) => (
						<ProductCell
							productCode={row.original.product?.productCode}
							productName={row.original.product?.productName}
							barcode={row.original.product?.barcode}
						/>
					),
				},
				{
					id: "source",
					header: "Source",
					accessorKey: "product.productSource.productSourceName",
					cell: ({ getValue }) => getValue() ?? "-",
				},
				{
					id: "brand",
					header: "Brand",
					accessorKey: "product.productBrand.productBrandName",
					cell: ({ getValue }) => getValue() ?? "-",
				},
				{
					id: "category",
					header: "Category",
					accessorKey: "product.productCategory.productCategoryName",
					cell: ({ getValue }) => getValue() ?? "-",
				},
				{
					id: "subCategory",
					header: "Sub Category",
					accessorKey: "product.productSubCategory.productSubCategoryName",
					cell: ({ getValue }) => getValue() ?? "-",
				},
				{
					id: "sellingPrice",
					header: "Selling Price",
					accessorKey: "sellingPrice",
				},
				{
					id: "actions",
					header: "Actions",
					cell: ({ row }) => (
						<div className="flex gap-2">
							<Button
								variant="ghost"
								size="sm"
								onClick={() => {
									onShowDetail(row.original);
								}}
								className="h-8 w-8 p-0"
							>
								<EyeIcon className="h-4 w-4" />
							</Button>
							<Can permissions="pricelist.edit">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										onShowEdit(row.original);
									}}
									className="h-8 w-8 p-0"
								>
									<EditIcon className="h-4 w-4" />
								</Button>
							</Can>
							<Can permissions="pricelist.edit">
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										onShowDelete(row.original);
									}}
									className="h-8 w-8 p-0 text-destructive hover:text-destructive cursor-pointer"
								>
									<Trash2Icon className="h-4 w-4" />
								</Button>
							</Can>
						</div>
					),
				},
			] satisfies ColumnDef<PricelistProduct, unknown>[],
		[onShowDetail, onShowDelete, onShowEdit],
	);

	const formFilters: DataTableFiltersProp = useMemo(
		() =>
			[
				{
					variant: "plain",
					layout: "flex",
					flex: { direction: "row", wrap: true },
					fields: [
						{
							name: "searchType",
							label: "",
							placeholder: "Search",
							type: "select",
							options: [
								{ label: "SKU", value: "productCode" },
								{ label: "Nama", value: "productName" },
							],
							defaultValue: "productCode",
						},
						{
							name: "keyword",
							label: "",
							placeholder: "Keyword",
							type: "text",
						},
						{
							name: "productSourceID",
							label: "",
							placeholder: "Source",
							type: "autocomplete",
							autocompleteMode: "server",
							fetcher: productSourceAutocompleteFetcher,
							options: [],
						},
						{
							name: "productBrandID",
							label: "",
							placeholder: "Brand",
							type: "autocomplete",
							autocompleteMode: "server",
							fetcher: productBrandAutocompleteFetcher,
							options: [],
						},
					],
				},
				{
					variant: "plain",
					layout: "flex",
					flex: { direction: "row", wrap: true },
					fields: [
						// {
						// 	name: "productDivisionID",
						// 	label: "",
						// 	placeholder: "Division",
						// 	type: "autocomplete",
						// 	autocompleteMode: "server",
						// 	fetcher: productDivisionAutocompleteFetcher,
						// },
						{
							name: "productDepartmentID",
							label: "",
							placeholder: "Department",
							type: "autocomplete",
							autocompleteMode: "server",
							options: [],
							fetcher: productDepartmentAutocompleteFetchcer,
						},
						{
							name: "productSubDepartmentID",
							label: "",
							placeholder: "Sub Department",
							type: "autocomplete",
							autocompleteMode: "server",
							options: [],
							fetcher: productSubDepartmentAutocompleteFetchcer,
						},
					],
				},
				{
					variant: "plain",
					layout: "flex",
					flex: { direction: "row", wrap: true },
					fields: [
						{
							name: "productCategoryID",
							label: "",
							placeholder: "Category",
							type: "autocomplete",
							autocompleteMode: "server",
							options: [],
							fetcher: productCategoryAutocompleteFetcher,
						},
						{
							name: "productSubCategoryID",
							label: "",
							placeholder: "Sub Category",
							type: "autocomplete",
							autocompleteMode: "server",
							options: [],
							fetcher: productSubCategoryAutocompleteFetcher,
						},
					],
				},
			] satisfies DataTableFiltersProp,
		[
			productBrandAutocompleteFetcher,
			productCategoryAutocompleteFetcher,
			productSubCategoryAutocompleteFetcher,
			productDepartmentAutocompleteFetchcer,
			productSubDepartmentAutocompleteFetchcer,
			// productDivisionAutocompleteFetcher,
			productSourceAutocompleteFetcher,
		],
	);

	const actions = useGuardedDataTableActions([
		{
			key: "create",
			icon: <PlusIcon />,
			onClick: onShowAddProduct,
			permissions: "pricelist.edit",
		},
		{
			key: "download-template",
			icon: <DownloadIcon />,
			label: "Download Template",
			variant: "outline",
			onClick: onDownloadTemplate,
			permissions: "pricelist.edit",
		},
		{
			key: "import",
			icon: <UploadIcon />,
			label: "Import",
			variant: "outline",
			onClick: onImport,
			permissions: "pricelist.edit",
		},
		{
			key: "refresh",
			icon: <RefreshCcwIcon />,
			variant: "outline",
			onClick: onRefresh,
		},
	]);

	return (
		<DataTable<PricelistProduct, unknown>
			columns={columns}
			data={data}
			rowCount={rowCount}
			loading={loading}
			filterWrapper="none"
			formFilters={formFilters}
			onFormFilterChange={onFormFilterChange}
			pagination
			paginationState={paginationState}
			onPaginationChange={onPaginationChange}
			showStandardActions={false}
			filterShowActionsSeparator={false}
			actions={actions}
		/>
	);
}
